import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getPaymentByIdOptions, refundPaymentMutation } from "@/lib/client/@tanstack/react-query.gen";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	ArrowLeft,
	CreditCard,
	User,
	Calendar,
	Euro,
	Receipt,
	AlertCircle,
	CheckCircle,
	XCircle,
	Clock,
	Undo,
	FileText,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/payments/$id")({
	component: PaymentDetailPage,
});

function PaymentDetailPage() {
	const { id } = Route.useParams();
	const queryClient = useQueryClient();

	const [refundType, setRefundType] = useState<"full" | "partial">("full");
	const [refundAmount, setRefundAmount] = useState("");
	const [refundComment, setRefundComment] = useState("");

	const { data: payment, isLoading } = useQuery(
		getPaymentByIdOptions({
			path: { id },
		}),
	);

	const refundMutation = useMutation({
		...refundPaymentMutation(),
		onSuccess: () => {
			toast.success("Remboursement effectué avec succès");
			queryClient.invalidateQueries({ queryKey: ["payments"] });
			setRefundAmount("");
			setRefundComment("");
			setRefundType("full");
		},
		onError: () => {
			toast.error("Erreur lors du remboursement");
		},
	});

	const statusConfig: Record<
		string,
		{
			label: string;
			variant: "default" | "secondary" | "destructive" | "outline";
			icon: any;
			color: string;
		}
	> = {
		PENDING: {
			label: "En attente",
			variant: "secondary",
			icon: Clock,
			color: "text-yellow-600",
		},
		PROCESSING: {
			label: "En cours",
			variant: "secondary",
			icon: Clock,
			color: "text-blue-600",
		},
		SUCCEEDED: {
			label: "Réussi",
			variant: "default",
			icon: CheckCircle,
			color: "text-green-600",
		},
		FAILED: {
			label: "Échec",
			variant: "destructive",
			icon: XCircle,
			color: "text-red-600",
		},
		REFUNDED: {
			label: "Remboursé",
			variant: "outline",
			icon: Undo,
			color: "text-gray-600",
		},
		CANCELLED: {
			label: "Annulé",
			variant: "outline",
			icon: XCircle,
			color: "text-gray-600",
		},
	};

	const handleRefund = () => {
		if (!payment || !refundComment) {
			toast.error("Veuillez renseigner un commentaire");
			return;
		}

		const amount = refundType === "full"
			? maxRefundAmount
			: parseFloat(refundAmount);

		if (refundType === "partial" && (!refundAmount || amount <= 0)) {
			toast.error("Veuillez renseigner un montant valide");
			return;
		}

		refundMutation.mutate({
			path: { paymentId: id },
			body: {
				amount,
				comment: refundComment,
			},
		});
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-96">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
			</div>
		);
	}

	if (!payment) {
		return (
			<div className="p-6">
				<div className="text-center">
					<h2 className="text-2xl font-bold text-gray-900 mb-4">
						Paiement non trouvé
					</h2>
					<Link to="/admin/payments">
						<Button variant="outline">
							<ArrowLeft className="mr-2 h-4 w-4" />
							Retour à la liste
						</Button>
					</Link>
				</div>
			</div>
		);
	}

	const status = statusConfig[payment.status] || statusConfig.PENDING;
	const StatusIcon = status.icon;
	const maxRefundAmount = payment.amount - (payment.refundAmount || 0);
	const canRefund = payment.status === "SUCCEEDED" && maxRefundAmount > 0;

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-4">
				<Link to="/admin/payments">
					<Button variant="ghost" size="sm">
						<ArrowLeft className="h-4 w-4 mr-2" />
						Retour
					</Button>
				</Link>
			</div>

			<div className="flex justify-between items-start">
				<div>
					<h1 className="text-3xl font-bold">
						Paiement #{payment.id?.substring(0, 8)}
					</h1>
					<p className="text-gray-600 mt-2">
						{format(new Date(payment.createdAt), "dd MMMM yyyy à HH:mm", {
							locale: fr,
						})}
					</p>
				</div>
				<Badge variant={status.variant} className="text-base py-1 px-3">
					<StatusIcon className={`h-4 w-4 mr-1 ${status.color}`} />
					{status.label}
				</Badge>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<CreditCard className="h-5 w-5" />
							Informations du paiement
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div>
							<p className="text-sm text-gray-600">Montant</p>
							<div className="flex items-center gap-2">
								<Euro className="h-5 w-5 text-green-600" />
								<p className="text-2xl font-bold">
									{payment.amount?.toFixed(2)} €
								</p>
							</div>
						</div>
						<div>
							<p className="text-sm text-gray-600">Méthode de paiement</p>
							<p className="font-medium">
								{payment.paymentMethod === "CARD" && "Carte bancaire"}
								{payment.paymentMethod === "SEPA" && "Prélèvement SEPA"}
								{payment.paymentMethod === "BANK_TRANSFER" &&
									"Virement bancaire"}
								{payment.paymentMethod === "MANUAL" && "Manuel"}
								{!payment.paymentMethod && "Non défini"}
							</p>
						</div>
						<div>
							<p className="text-sm text-gray-600">Fournisseur</p>
							<Badge variant="secondary">
								{payment.paymentProvider === "STRIPE" && "Stripe"}
								{payment.paymentProvider === "HELLOASSO" && "HelloAsso"}
								{payment.paymentProvider === "INTERNAL_DEBT_FORGIVENESS" && "Effacement de dette"}
								{!payment.paymentProvider && "-"}
							</Badge>
						</div>
						<div>
							<p className="text-sm text-gray-600">Date du paiement</p>
							<div className="flex items-center gap-2">
								<Calendar className="h-4 w-4 text-gray-400" />
								<p className="font-medium">
									{format(new Date(payment.createdAt), "dd/MM/yyyy à HH:mm", {
										locale: fr,
									})}
								</p>
							</div>
						</div>
						{payment.providerPaymentId && (
							<div>
								<p className="text-sm text-gray-600">ID de transaction</p>
								<p className="font-mono text-sm">{payment.providerPaymentId}</p>
							</div>
						)}
						{payment.comment && (
							<>
								<Separator />
								<div>
									<p className="text-sm text-gray-600">Commentaire</p>
									<p className="text-sm bg-gray-50 p-3 rounded mt-1">
										{payment.comment}
									</p>
								</div>
							</>
						)}
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<User className="h-5 w-5" />
							Informations du parent
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div>
							<p className="text-sm text-gray-600">Parent</p>
							<p className="font-medium">
								{payment.parent.firstName} {payment.parent.lastName}
							</p>
						</div>
						<div>
							<p className="text-sm text-gray-600">Email</p>
							<p className="font-medium">{payment.parent.email}</p>
						</div>
						<div>
							<p className="text-sm text-gray-600">Téléphone</p>
							<p className="font-medium">{payment.parent.phone || "-"}</p>
						</div>
						<div className="pt-2">
							<Link to="/admin/parents/$id" params={{ id: payment.parent.id }}>
								<Button size="sm" variant="outline">
									<User className="h-3 w-3 mr-2" />
									Voir le parent
								</Button>
							</Link>
						</div>
					</CardContent>
				</Card>
			</div>

			{payment.failureMessage && (
				<Card className="border-red-200 bg-red-50">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-red-800">
							<AlertCircle className="h-5 w-5" />
							Erreur de paiement
						</CardTitle>
					</CardHeader>
					<CardContent>
						<p className="text-red-700">{payment.failureMessage}</p>
					</CardContent>
				</Card>
			)}

			{payment.invoiceAllocations && payment.invoiceAllocations.length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<Receipt className="h-5 w-5" />
							Répartition sur les factures
						</CardTitle>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Numéro de facture</TableHead>
									<TableHead>Date</TableHead>
									<TableHead className="text-right">Montant alloué</TableHead>
									<TableHead className="text-right">Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{payment.invoiceAllocations.map((allocation) => (
									<TableRow key={allocation.id}>
										<TableCell className="font-medium">
											<div className="flex items-center gap-2">
												<FileText className="h-4 w-4 text-gray-400" />
												{allocation.invoiceNumber}
											</div>
										</TableCell>
										<TableCell className="text-sm text-gray-600">
											{format(new Date(allocation.createdAt), "dd/MM/yyyy", {
												locale: fr,
											})}
										</TableCell>
										<TableCell className="text-right font-medium">
											{allocation.allocatedAmount.toFixed(2)} €
										</TableCell>
										<TableCell className="text-right">
											<Link
												to="/admin/invoices/$id"
												params={{ id: allocation.invoiceId }}
											>
												<Button size="sm" variant="ghost">
													Voir la facture
												</Button>
											</Link>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>

						<div className="mt-4 pt-4 border-t flex justify-between items-center">
							<span className="text-sm font-medium text-gray-500">Total</span>
							<span className="text-lg font-bold">
								{payment.invoiceAllocations
									.reduce((sum, allocation) => sum + allocation.allocatedAmount, 0)
									.toFixed(2)}{" "}
								€
							</span>
						</div>
					</CardContent>
				</Card>
			)}

			{payment.refunded && (
				<Card className="border-orange-200 bg-orange-50">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-orange-800">
							<Undo className="h-5 w-5" />
							Informations de remboursement
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-2">
						<div className="flex justify-between">
							<span className="text-sm text-gray-600">Montant remboursé</span>
							<span className="font-bold text-orange-800">
								{payment.refundAmount?.toFixed(2)} €
							</span>
						</div>
						{payment.refundDate && (
							<div className="flex justify-between">
								<span className="text-sm text-gray-600">Date du remboursement</span>
								<span className="font-medium">
									{format(new Date(payment.refundDate), "dd/MM/yyyy à HH:mm", {
										locale: fr,
									})}
								</span>
							</div>
						)}
						{maxRefundAmount > 0 && (
							<Alert className="mt-4">
								<AlertCircle className="h-4 w-4" />
								<AlertDescription>
									Remboursement partiel - Montant restant remboursable:{" "}
									<strong>{maxRefundAmount.toFixed(2)} €</strong>
								</AlertDescription>
							</Alert>
						)}
					</CardContent>
				</Card>
			)}

			{canRefund && (
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<Undo className="h-5 w-5" />
							Effectuer un remboursement
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<Alert>
							<AlertCircle className="h-4 w-4" />
							<AlertDescription>
								Montant maximum remboursable: <strong>{maxRefundAmount.toFixed(2)} €</strong>
								<br />
								<span className="text-xs text-gray-600">
									Cette action ne déclenche pas de remboursement réel via Stripe ou HelloAsso.
									Elle sert uniquement à enregistrer qu'un remboursement a eu lieu.
								</span>
							</AlertDescription>
						</Alert>

						<RadioGroup value={refundType} onValueChange={(v: any) => setRefundType(v)}>
							<div className="flex items-center space-x-2">
								<RadioGroupItem value="full" id="full" />
								<Label htmlFor="full">
									Remboursement total ({maxRefundAmount.toFixed(2)} €)
								</Label>
							</div>
							<div className="flex items-center space-x-2">
								<RadioGroupItem value="partial" id="partial" />
								<Label htmlFor="partial">Remboursement partiel</Label>
							</div>
						</RadioGroup>

						{refundType === "partial" && (
							<div>
								<Label htmlFor="refundAmount">Montant à rembourser (€)</Label>
								<Input
									id="refundAmount"
									type="number"
									step="0.01"
									max={maxRefundAmount}
									value={refundAmount}
									onChange={(e) => setRefundAmount(e.target.value)}
									placeholder="0.00"
								/>
								<p className="text-xs text-gray-500 mt-1">
									Maximum: {maxRefundAmount.toFixed(2)} €
								</p>
							</div>
						)}

						<div>
							<Label htmlFor="refundComment">Commentaire (obligatoire)</Label>
							<Textarea
								id="refundComment"
								value={refundComment}
								onChange={(e) => setRefundComment(e.target.value)}
								placeholder="Expliquez la raison du remboursement..."
								rows={3}
								className="border-orange-300"
							/>
							<p className="text-xs text-orange-600 mt-1">
								Le commentaire est obligatoire pour justifier le remboursement
							</p>
						</div>

						<Button
							onClick={handleRefund}
							disabled={!refundComment || refundMutation.isPending || (refundType === "partial" && !refundAmount)}
							className="w-full"
						>
							{refundMutation.isPending ? (
								"Traitement..."
							) : (
								<>
									<Undo className="mr-2 h-4 w-4" />
									Effectuer le remboursement
								</>
							)}
						</Button>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
